<template>
    <Teleport to="body">
        <div
            v-show="isOpen && hasVisibleSections"
            ref="floating"
            class="settings-panel card-main"
            :class="{
                visible: isVisible,
                horizontal: isHorizontalLayout,
                mobile: isMobileView && isOpen,
            }"
            :style="popoverStyles"
            @mouseenter="cancelHide"
            @focusin="cancelHide"
            @mouseleave="handlePanelLeave"
            @focusout="handlePanelLeave"
        >
            <div
                ref="floatingArrow"
                class="floating-arrow"
                :style="arrowStyle"
            />
            <div v-if="hasLeftSection" class="left">
                <slot
                    name="left"
                    :updatePopoverVisibility="updatePopoverVisibility"
                    :displayOptionsTop="shouldDisplayOptionsTop"
                />
            </div>
            <hr v-if="hasLeftSection && (hasMiddleSection || hasRightSection)" />
            <div v-if="hasMiddleSection" class="middle">
                <action-button
                    v-for="action in middleActions"
                    :key="action.id"
                    :class="['icon-controls', action.className]"
                    :title="action.title"
                    :tooltip-left="action.tooltipLeft"
                    :icon="action.icon"
                    show-title-on-mobile-view
                    @click="action.click"
                />
            </div>
            <hr v-if="hasMiddleSection && hasRightSection" />
            <div v-if="hasRightSection" class="right">
                <action-button
                    v-for="action in rightActions"
                    :key="action.id"
                    :class="['icon-controls', action.className]"
                    :title="action.title"
                    :tooltip-left="action.tooltipLeft"
                    :icon="action.icon"
                    show-title-on-mobile-view
                    @click="action.click"
                />

            </div>
            <popover ref="optionsPopover" dismissable @hide="updatePopoverVisibility(false)">
                <dashboard-selector
                    v-if="popoverType === 'clone-module'"
                    class="module-footer-dashboard-selector"
                    @update:selected-dashboards="selectedDashboardIds = $event"
                />
                <div v-if="popoverType === 'clone-module'" class="module-footer-clone-actions">
                    <primevue-button
                        label="Clone module"
                        :disabled="selectedDashboardIds.length === 0"
                        @click="cloneModule(selectedDashboardIds)"
                    />
                </div>
            </popover>
        </div>
    </Teleport>
    <div v-if="props.showMobileTrigger && isMobileView && hasVisibleSections" class="module-footer-mobile-trigger">
        <action-button
            aria-label="Module options"
            class="icon-controls"
            title="Module options"
            :icon="['fas', 'sliders']"
            @click="toggleSettings"
        />
    </div>
    <div
        v-if="!props.blockedActions?.changeLayout"
        class="false-button resize-button"
        :class="{ 'hide-resize': props.blockedActions?.changeLayout }"
        @mouseup="releaseResizeButton">
        <font-awesome-icon
            class="icon"
            :icon="['fas', 'up-right-and-down-left-from-center']"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, nextTick, onBeforeUnmount, useSlots, StyleValue,
} from 'vue';
import {
    useFloating, autoUpdate, offset, flip, shift, arrow, type Placement,
    Middleware,
} from '@floating-ui/vue';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import Popover from 'primevue/popover';
import { useConfirm } from 'primevue/useconfirm';
import { useUIStore } from '@/stores/uiStore.ts';
import { storeToRefs } from 'pinia';
import { IModuleBlockedActions } from '@/interfaces/actions/enact/IModuleActions.ts';
import DashboardSelector from '@/components/powerMarkets/common/DashboardSelector.vue';
import PrimevueButton from 'primevue/button';
import { usePrimeVue } from 'primevue/config';

const DEFAULT_COLLISION_PADDING_TOP = 60;

const confirm = useConfirm();
const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);
const slots = useSlots();
const primevue = usePrimeVue();

const props = defineProps<{
    moduleName: string;
    blockedActions?: IModuleBlockedActions;
    hasLeftOptions?: boolean;
    displayOptionsTop?: boolean;
    showMobileTrigger?: boolean;
    isModal?: boolean;
    collisionPaddingTop?: number;
}>();

const emit = defineEmits<{
    'delete:module': [];
    'clone:module': [Array<string>];
    'release:resize-button': [];
    'edit:module': [];
    'fullscreen:module': [];
    'pop-out:module': [];
}>();

type TFooterActionId = 'fullscreen' | 'popout' | 'clone' | 'edit' | 'delete';
type TFooterAction = {
    id: TFooterActionId;
    title: string;
    icon: [string, string];
    className?: string;
    tooltipLeft?: boolean;
    click: (event: Event) => void;
};

const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const floatingArrow = ref<HTMLElement | null>(null);

const ARROW_MIDDLEWARE = arrow({ element: floatingArrow });
const OFFSET_MIDDLEWARE = offset({ mainAxis: 5, alignmentAxis: 0 });

const middleActions = computed<Array<TFooterAction>>(() => {
    const actions: Array<TFooterAction> = [];

    if (!props.blockedActions?.fullscreen) {
        actions.push({
            id: 'fullscreen',
            title: 'Show as full screen',
            icon: ['fas', 'up-right-and-down-left-from-center'],
            click: () => emit('fullscreen:module'),
        });
    }

    if (!props.blockedActions?.popout) {
        actions.push({
            id: 'popout',
            title: 'Expand',
            icon: ['fas', 'arrow-up-right-from-square'],
            click: openChartPopout,
        });
    }

    return actions;
});

const rightActions = computed<Array<TFooterAction>>(() => {
    const actions: Array<TFooterAction> = [];

    if (!props.blockedActions?.clone && !isMobileView.value) {
        actions.push({
            id: 'clone',
            title: 'Clone this module',
            icon: ['fas', 'clone'],
            click: (event: Event): void => openPopover(event, 'clone-module'),
        });
    }

    if (!props.blockedActions?.edit && !isMobileView.value) {
        actions.push({
            id: 'edit',
            title: 'Edit this module',
            icon: ['fas', 'pen-ruler'],
            className: 'edit-btn',
            tooltipLeft: true,
            click: editModule,
        });
    }

    if (!props.blockedActions?.delete) {
        actions.push({
            id: 'delete',
            title: 'Delete module',
            icon: ['fas', 'trash-can'],
            className: 'delete',
            tooltipLeft: true,
            click: handleConfirmDeleteRequest,
        });
    }

    return actions;
});

const hasLeftSection = computed<boolean>(() => props.hasLeftOptions ?? !!slots.left);
const hasMiddleSection = computed<boolean>(() => middleActions.value.length > 0);
const hasRightSection = computed<boolean>(() => rightActions.value.length > 0);
const hasVisibleSections = computed<boolean>(() => hasLeftSection.value || hasMiddleSection.value || hasRightSection.value);
const shouldDisplayOptionsTop = computed<boolean>(() => !isMobileView.value || !!props.displayOptionsTop);
const isHorizontalLayout = computed<boolean>(() => !isMobileView.value);
const collisionPaddingTop = computed<number>(() => props.collisionPaddingTop ?? DEFAULT_COLLISION_PADDING_TOP);

const floatingPlacement = computed<Placement>(() => {
    if (isMobileView.value) {
        return 'bottom-start';
    }
    return 'top';
});

const floatingMiddleware = computed<Array<Middleware>>(() => {
    const shiftPadding = {
        top: Math.max(12, collisionPaddingTop.value),
        right: 12,
        bottom: 12,
        left: 12,
    };

    return [
        OFFSET_MIDDLEWARE,
        ARROW_MIDDLEWARE,
        flip({
            fallbackPlacements: ['bottom'],
            padding: { top: collisionPaddingTop.value },
        }),
        shift({ padding: shiftPadding }),
    ];
});

const { floatingStyles, placement, middlewareData } = useFloating(reference, floating, {
    placement: floatingPlacement,
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware,
});

const isOpen = ref(false);
const isVisible = ref(false);
let hideTimer: number | null = null;
let closeTimer: number | null = null;

let outsidePointerDownHandler: ((e: PointerEvent) => void) | null = null;

function addOutsideCloseListener (): void {
    if (outsidePointerDownHandler) return;
    outsidePointerDownHandler = (e: PointerEvent): void => {
        if (!isOpen.value || !isVisible.value) return;
        if (!isMobileView.value) return;
        if (popoverShown.value) return;

        const target = e.target as Node | null;
        if (!target) return;

        if (floating.value?.contains(target)) return;
        if (reference.value?.contains(target)) return;

        closeImmediately();
    };

    window.addEventListener('pointerdown', outsidePointerDownHandler, true);
}

function removeOutsideCloseListener (): void {
    if (!outsidePointerDownHandler) return;
    window.removeEventListener('pointerdown', outsidePointerDownHandler, true);
    outsidePointerDownHandler = null;
}

const popoverStyles = computed<StyleValue>(() => {
    let zIndex = primevue.config.zIndex?.overlay ?? 1000;
    if (props.isModal) zIndex = parseInt(uiStore.cssVariables.popoverOnModalZIndex, 10);

    return {
        ...floatingStyles.value,
        zIndex,
    };
});

const arrowStyle = computed<StyleValue>(() => {
    if (!middlewareData.value.arrow) return {};

    const { x, y } = middlewareData.value.arrow;
    const side = placement.value.split('-')[0];

    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[side] as string;

    const rotation = {
        top: 45,
        right: 135,
        bottom: -135,
        left: -45,
    }[side];

    return {
        left: x != null ? `${x}px` : '',
        top: y != null ? `${y / 2}px` : '',
        [staticSide]: '-5.5px',
        transform: `rotate(${rotation}deg)`,
    };
});

function resolveSettingsTrigger (e: Event): HTMLElement | null {
    const trigger = (e.currentTarget ?? e.target) as HTMLElement | null;
    if (!trigger) return null;

    return trigger.closest?.('.module-options-trigger, .module-footer-mobile-trigger, .common-action-button') as HTMLElement ?? trigger;
}

function showSettings (e: Event): void {
    if (!hasVisibleSections.value) return;
    cancelHide();

    const trigger = resolveSettingsTrigger(e);
    if (!trigger) return;

    if (isMobileView.value && isOpen.value && reference.value === trigger && isVisible.value) {
        closeImmediately();
        return;
    }

    reference.value = trigger;

    isOpen.value = true;

    nextTick(() => {
        isVisible.value = true;
    });

    if (isMobileView.value) {
        addOutsideCloseListener();
    }
}

function closeImmediately (): void {
    cancelHide();
    isVisible.value = false;
    isOpen.value = false;
    reference.value = null;
    removeOutsideCloseListener();
}

function hideSettings (): void {
    if (popoverShown.value) return;

    hideTimer = window.setTimeout(() => {
        isVisible.value = false;
        closeTimer = window.setTimeout(() => {
            if (isVisible.value) return;
            isOpen.value = false;
            reference.value = null;
        }, 120);
    }, 150);
}

function cancelHide (): void {
    if (hideTimer) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
    }

    if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
    }
}

function handlePanelLeave (): void {
    if (isMobileView.value) return;
    hideSettings();
}

const hovered = ref(false);
const isMouseOver = ref(false);
const popoverShown = ref(false);

type popoverTypes = 'clone-module';
const popoverType = ref<null | popoverTypes>(null);
const optionsPopover = ref<InstanceType<typeof Popover> | null>(null);
const selectedDashboardIds = ref<Array<string>>([]);

function handleConfirmDeleteRequest (): void {
    confirm.require({
        message: `Are you sure you wish to delete ${props.moduleName}?`,
        accept: () => {
            emit('delete:module');
        },
    });
}

function releaseResizeButton (): void {
    emit('release:resize-button');
}

function editModule (): void {
    if (isMobileView.value) return;
    emit('edit:module');
}

function openPopover (event: Event, type: popoverTypes): void {
    if (isMobileView.value && type === 'clone-module') return;
    if (!optionsPopover.value) return;
    if (type === 'clone-module') selectedDashboardIds.value = [];
    popoverType.value = type;
    optionsPopover.value?.toggle(event);
    popoverShown.value = true;
}

function updatePopoverVisibility (value: boolean): void {
    popoverShown.value = value;
    setTimeout(() => {
        if (!isMouseOver.value) {
            hovered.value = false;
        }
    }, 300);
}

function cloneModule (dashboardIds: Array<string>): void {
    emit('clone:module', dashboardIds);
    optionsPopover.value?.hide();
}

function openChartPopout (): void {
    emit('pop-out:module');
}

function toggleSettings (e: Event): void {
    showSettings(e);
}

onBeforeUnmount(() => {
    cancelHide();
    removeOutsideCloseListener();
});

defineExpose({
    showSettings,
    hideSettings,
});

</script>

<style lang="scss" scoped>
.settings-panel {
    z-index: 1000;

    opacity: 0;
    transition: opacity 120ms ease;

    display: flex;
    flex-direction: column;

    border-radius: 8px;
    padding: var(--smallSpacing);
    box-shadow: var(--shadow2);
    border-color: var(--neutral700);

    &.horizontal {
        flex-direction: row;

        .left, .middle, .right {
            flex-direction: row;

            :deep(.icon-flex) {
                flex-direction: row;
            }
        }
    }

    &.visible {
        opacity: 1;
    }

    &.mobile {
        max-width: calc(100vw - var(--defaultSpacing));
    }

    .right, .middle, .left {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        gap: var(--smallSpacing) 0;

        :deep(.icon-flex) {
            display: flex;
            flex-direction: column;
            gap: var(--smallSpacing) 0;
        }
    }
}

.floating-arrow {
    position: absolute;
    width: 10px;
    height: 10px;
    transform: rotate(45deg);
    z-index: 1;
    background: inherit;
    border-bottom: 1px solid var(--neutral700);
    border-right: 1px solid var(--neutral700);
    border-top: none;
    border-left: none;
}

@container module-footer (max-width: 400px) {
    .module-footer{
        grid-template-rows: auto;
        grid-template-columns: 1fr;
    }
}

.false-button{
    font-size: var(--defaultFontSize);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--p-button-padding-y) var(--p-button-padding-x);

    .icon{
        color: var(--actionColour);
    }
}

.resize-button{
    cursor: nw-resize;
    position: absolute;
    bottom: var(--smallSpacing);
    right: var(--smallSpacing);
    background: var(--neutral0);
    border: solid 1px var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    padding: var(--defaultSpacing);
    opacity: 0;
    transition: opacity 300ms;
    z-index: 1;
    height: var(--resizeButtonSize);
    width: var(--resizeButtonSize);

    .icon{
        transform: rotate(90deg);
    }
}

.module-footer-mobile-trigger {
    position: absolute;
    top: var(--smallSpacing);
    right: var(--smallSpacing);
    z-index: 2;

    :deep(.common-action-button) {
        background: var(--neutral0);
        border: solid 1px var(--neutral300);
        border-radius: var(--borderRadiusOuter);
        padding: var(--defaultSpacing);
        height: var(--resizeButtonSize);
        width: var(--resizeButtonSize);
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.module-footer-clone-actions {
    display: flex;
    justify-content: flex-end;
    padding: var(--defaultSpacing) var(--largeSpacing) var(--largeSpacing);
}

</style>
